import React from 'react';
import HtmlEditor from '../HtmlEditor';
import { ReportData } from '../../types/report.types';
import { sortFindings } from '../../utils/reportSharedUtils';

const RISK_LEVELS = ['Critical', 'High', 'Medium', 'Low', 'Info'] as const;

const RISK_COLORS: Record<string, { bg: string; border: string; text: string; activeBg: string; activeText: string }> = {
  Critical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', activeBg: 'bg-red-600', activeText: 'text-white' },
  High:     { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', activeBg: 'bg-orange-500', activeText: 'text-white' },
  Medium:   { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-700', activeBg: 'bg-yellow-400', activeText: 'text-white' },
  Low:      { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', activeBg: 'bg-green-600', activeText: 'text-white' },
  Info:     { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-600', activeBg: 'bg-slate-500', activeText: 'text-white' },
};

interface ExecutiveSummaryEditorV2Props {
  reportData: ReportData;
  onHtmlChange: (field: keyof ReportData, value: string) => void;
  onRecommendationsChange: (recommendations: { title: string; description: string }[]) => void;
  onDataChange: (field: string, value: any) => void;
}

const ExecutiveSummaryEditorV2: React.FC<ExecutiveSummaryEditorV2Props> = ({
  reportData,
  onHtmlChange,
  onRecommendationsChange,
  onDataChange
}) => {
  const getDynamicRecommendations = React.useCallback((data: ReportData) => {
    const rawFindings = data.detailed_findings || [];
    const openFindings = rawFindings.filter(f => f.status?.toLowerCase() === 'open');
    const sorted = sortFindings(openFindings);
    
    let parsedConfig: any = {};
    if (data.finding_config) {
      if (typeof data.finding_config === 'string') {
        try {
          parsedConfig = JSON.parse(data.finding_config);
        } catch (_) {}
      } else {
        parsedConfig = data.finding_config;
      }
    }
    const config = parsedConfig || {};
    const roadmap = config.remediationRoadmap || {};

    const getFindingRecAction = (f: any, index: number) => {
      const fid = f.report_id || f.id || f.abbreviation || `F-${index + 1}`;
      if (roadmap[fid]?.action) return `${roadmap[fid].action} (${fid})`;
      const action = f.title ? `Remediate ${f.title}` : 'Remediate vulnerability';
      return `${action} (${fid})`;
    };

    // Group open findings by severity/priority
    const shortTermFindings = sorted.filter(f => {
      const sev = (f.severity_category || '').toLowerCase();
      return sev === 'critical' || sev === 'high';
    });

    const mediumTermFindings = sorted.filter(f => {
      const sev = (f.severity_category || '').toLowerCase();
      return sev === 'medium';
    });

    const longTermFindings = sorted.filter(f => {
      const sev = (f.severity_category || '').toLowerCase();
      return sev === 'low' || sev === 'info';
    });

    const recs = [];

    // Short term recommendation
    if (shortTermFindings.length > 0) {
      const actions = shortTermFindings.map((f, idx) => getFindingRecAction(f, idx)).join(', ');
      recs.push({
        title: 'Short (0-30 days)',
        description: `Remediate critical and high severity findings: ${actions}.`
      });
    }

    // Medium term recommendation
    if (mediumTermFindings.length > 0) {
      const actions = mediumTermFindings.map((f, idx) => getFindingRecAction(f, idx)).join(', ');
      recs.push({
        title: 'Medium (1-3 mo)',
        description: `Remediate medium severity findings: ${actions}. Centralize input validation and access controls.`
      });
    }

    // Long term recommendation
    if (longTermFindings.length > 0) {
      const actions = longTermFindings.map((f, idx) => getFindingRecAction(f, idx)).join(', ');
      recs.push({
        title: 'Long (3-12 mo)',
        description: `Remediate low and informational findings: ${actions}. Establish an SDLC security program with recurring penetration tests.`
      });
    }

    return recs;
  }, []);

  const isMockData = React.useCallback((recs: any[]) => {
    if (!recs || recs.length !== 3) return false;
    const desc0 = recs[0].description || '';
    const desc1 = recs[1].description || '';
    const desc2 = recs[2].description || '';
    return (desc0.includes('Remediate WEB-01') || desc0.includes('Remediate critical and high')) && 
           desc1.includes('Add SAST/DAST') && 
           desc2.includes('Establish an SDLC');
  }, []);

  const MOCK_KEY_FINDING_MARKERS = ['SQL injection in product search', 'IDOR', 'TLS configuration and login rate-limiting were robust'];
  const isMockKeyFindings = React.useCallback((kf: string) =>
    MOCK_KEY_FINDING_MARKERS.some(m => kf.includes(m)), []);

  const buildKeyFindingsHtml = React.useCallback((data: ReportData): string => {
    const allFindings: any[] = data.detailed_findings || [];
    const sevOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
    const sorted = [...allFindings]
      .filter((f: any) => {
        const s = (f.severity_category || f.severity || '').toLowerCase();
        return s === 'critical' || s === 'high';
      })
      .sort((a, b) => {
        const aS = (a.severity_category || a.severity || '').toLowerCase();
        const bS = (b.severity_category || b.severity || '').toLowerCase();
        return (sevOrder[aS] || 99) - (sevOrder[bS] || 99);
      });
    if (sorted.length === 0) return '';
    const items = sorted.map((f: any) => {
      const title = (f.title || 'Untitled finding').trim();
      return `  <li>${title}.</li>`;
    }).join('\n');
    return `<ul>\n${items}\n</ul>`;
  }, []);

  React.useEffect(() => {
    const currentRecs = reportData.recommendations_list || [];
    if (currentRecs.length === 0 || isMockData(currentRecs)) {
      onRecommendationsChange(getDynamicRecommendations(reportData));
    }
    const kf = reportData.key_findings || '';
    if (!kf.trim() || isMockKeyFindings(kf)) {
      const html = buildKeyFindingsHtml(reportData);
      if (html) onHtmlChange('key_findings', html);
    }
  }, []);


  const recommendations = Array.isArray(reportData.recommendations_list) && reportData.recommendations_list.length > 0 && !isMockData(reportData.recommendations_list)
    ? reportData.recommendations_list
    : getDynamicRecommendations(reportData);

  const handleRecChange = (index: number, field: 'title' | 'description', value: string) => {
    const updated = recommendations.map((rec, i) =>
      i === index ? { ...rec, [field]: value } : rec
    );
    onRecommendationsChange(updated);
  };

  const handleAddRec = () => {
    onRecommendationsChange([
      ...recommendations,
      { title: '', description: '' }
    ]);
  };

  const handleRemoveRec = (index: number) => {
    const updated = recommendations.filter((_, i) => i !== index);
    onRecommendationsChange(updated);
  };

  return (
    <div className="h-full overflow-y-auto space-y-6">
      {/* Description / Overview Section */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Executive Overview</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Write the non-technical summary of the assessment</p>
          </div>
        </div>
        <div className="pt-2">
          <HtmlEditor
            field="executive_summary"
            title=""
            reportData={reportData}
            onHtmlChange={onHtmlChange}
          />
        </div>
      </div>

      {/* Overall Risk Section */}
      {(() => {
        const config = reportData.finding_config || {
          globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true },
          individualFindings: {}
        };
        const overrideVal = config.overallRiskOverride || '';
        const descVal = config.overallRiskDescription || '';

        const updateConfigField = (field: 'overallRiskOverride' | 'overallRiskDescription', value: any) => {
          const updated = {
            ...config,
            [field]: value
          };
          onDataChange('finding_config', updated);
        };

        return (
          <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-8 bg-gradient-to-b from-red-500 to-red-700 rounded-full" />
              <div className="flex-1">
                <h3 className="text-base font-bold text-blue-900 tracking-tight">Overall Risk Rating</h3>
                <p className="text-xs text-blue-700/80 font-medium mt-0.5">Override the auto-detected risk level and write a custom risk description</p>
              </div>
              {overrideVal && (
                <button
                  onClick={() => updateConfigField('overallRiskOverride', '')}
                  className="text-[10px] font-semibold text-slate-400 hover:text-red-500 transition-colors border border-slate-200 rounded px-2 py-1"
                >
                  Reset to auto
                </button>
              )}
            </div>

            {/* Risk level pill selector */}
            <div className="mb-4">
              <label className="block text-[11px] font-semibold text-slate-600 mb-2 uppercase tracking-wider">Risk Level</label>
              <div className="flex gap-2 flex-wrap">
                {RISK_LEVELS.map(level => {
                  const colors = RISK_COLORS[level];
                  const isActive = overrideVal === level;
                  return (
                    <button
                      key={level}
                      onClick={() => updateConfigField('overallRiskOverride', isActive ? '' : level)}
                      className={`px-4 py-2 rounded-full text-xs font-bold border transition-all shadow-sm ${
                        isActive
                          ? `${colors.activeBg} ${colors.activeText} border-transparent scale-105 shadow-md`
                          : `${colors.bg} ${colors.border} ${colors.text} hover:scale-105`
                      }`}
                    >
                      {level}
                    </button>
                  );
                })}
              </div>
              {!overrideVal && (
                <p className="text-[10px] text-slate-400 mt-2 italic">No override — risk level is computed automatically from severity counts</p>
              )}
            </div>

            {/* Risk description */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Risk Description</label>
              <textarea
                value={descVal}
                onChange={e => updateConfigField('overallRiskDescription', e.target.value)}
                rows={3}
                placeholder="e.g. an unauthenticated SQL injection exposes the full customer database, posing a direct breach and compliance risk."
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none text-slate-700 leading-relaxed"
              />
              <p className="text-[10px] text-slate-400 mt-1">Leave blank to use the auto-generated description based on findings</p>
            </div>

            {/* Live preview */}
            {(overrideVal || descVal) && (() => {
              const level = overrideVal || 'Critical';
              const colors = RISK_COLORS[level] || RISK_COLORS.Critical;
              const desc = descVal || '...';
              return (
                <div className={`mt-4 flex items-start gap-4 rounded-lg border p-3 ${colors.bg} ${colors.border}`}>
                  <div className="flex flex-col min-w-[100px]">
                    <span className={`text-[10px] font-black uppercase ${colors.text}`}>OVERALL</span>
                    <span className={`text-sm font-black uppercase ${colors.text}`}>RISK: {level}</span>
                  </div>
                  <p className={`text-xs leading-relaxed ${colors.text} opacity-80`}>- {desc}</p>
                </div>
              );
            })()}
          </div>
        );
      })()}

      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Key findings (bottom line up front)</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Define key successes or failures (renders as bullets in the key findings section)</p>
          </div>
        </div>
        <div className="pt-2">
          <HtmlEditor
            field="key_findings"
            title=""
            reportData={reportData}
            onHtmlChange={onHtmlChange}
            useJodit={true}
          />
        </div>
      </div>

      {/* Strategic Recommendations Section */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
            <div>
              <h3 className="text-base font-bold text-blue-900 tracking-tight">Strategic Recommendations</h3>
              <p className="text-xs text-blue-700/80 font-medium mt-0.5">Add short, medium, and long-term recommended actions</p>
            </div>
          </div>
          <button
            onClick={handleAddRec}
            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Action
          </button>
        </div>
        <div className="space-y-4 pt-2">
          {recommendations.map((rec, index) => (
            <div key={index} className="group relative bg-slate-50 border border-slate-200 rounded-lg shadow-sm flex flex-col p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-slate-800">Recommendation #{index + 1}</span>
                <button
                  onClick={() => handleRemoveRec(index)}
                  className="text-red-600 hover:text-red-900 text-xs font-medium flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Timeframe Horizon (e.g. Short, Medium, Long)</label>
                  <input
                    type="text"
                    value={rec.title}
                    onChange={e => handleRecChange(index, 'title', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="e.g. Short (0-30 days)"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Recommended Action</label>
                  <textarea
                    value={rec.description}
                    onChange={e => handleRecChange(index, 'description', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Describe the action to take..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSummaryEditorV2;
